import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";

/* ================= ENUMS ================= */

const PartnerCapability = {
  BLOOD: "BLOOD",
  AMBULANCE: "AMBULANCE",
  BOTH: "BOTH",
};

const PartnerType = {
  HOSPITAL: "HOSPITAL",
  NGO: "NGO",
  PRIVATE: "PRIVATE",
  FREELANCE: "FREELANCE",
};

const API_URL =
  "https://partner.instablood.org/add_partner.php";

/* ================= COMPONENT ================= */

const AddPartner = () => {
  const navigate = useNavigate();

  /* ===== FORM STATE ===== */
  const [partnerName, setPartnerName] = useState("");
  const [partnerType, setPartnerType] = useState(
    PartnerType.HOSPITAL
  );
  const [capability, setCapability] = useState(
    PartnerCapability.BOTH
  );

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [loading, setLoading] = useState(false);

  /* ===== SUBMIT ===== */
  const handleSubmit = async () => {
    if (!partnerName || !city || !contactName || !phone) {
      alert("Please fill all required fields");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        partner_name: partnerName,
        partner_type: partnerType,
        capability,
        address,
        city,
        state,
        contact_name: contactName,
        phone,
        email,
      };

      const res = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.data?.success) {
        const partnerId = res.data.data.id;

        // 🔁 Navigation based on capability
        if (capability === PartnerCapability.AMBULANCE) {
          navigate(`/partners/${partnerId}/ambulances/add`);
        } else {
          navigate(`/partners/${partnerId}`);
        }
      } else {
        alert(res.data?.message || "Failed to add partner");
      }
    } catch (err) {
      console.error(err);
      alert("API error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" mb={2}>
        Add Partner
      </Typography>

      <Paper sx={{ p: 3 }}>
        {/* ================= PARTNER DETAILS ================= */}
        <Typography variant="subtitle1" mb={1}>
          Partner Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Partner Name"
              value={partnerName}
              onChange={(e) =>
                setPartnerName(e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Partner Type</InputLabel>
              <Select
                value={partnerType}
                label="Partner Type"
                onChange={(e) =>
                  setPartnerType(e.target.value)
                }
              >
                <MenuItem value="HOSPITAL">
                  Hospital
                </MenuItem>
                <MenuItem value="NGO">
                  NGO
                </MenuItem>
                <MenuItem value="PRIVATE">
                  Private
                </MenuItem>
                <MenuItem value="FREELANCE">
                  Free Lance
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ================= CAPABILITY ================= */}
        <Typography variant="subtitle1" mb={1}>
          Partner Capability
        </Typography>

        <FormLabel sx={{ mb: 1 }}>
          This partner can provide:
        </FormLabel>

        <RadioGroup
          row
          value={capability}
          onChange={(e) =>
            setCapability(e.target.value)
          }
        >
          <FormControlLabel
            value="BLOOD"
            control={<Radio />}
            label="Blood Only"
          />
          <FormControlLabel
            value="AMBULANCE"
            control={<Radio />}
            label="Ambulance Only"
          />
          <FormControlLabel
            value="BOTH"
            control={<Radio />}
            label="Blood + Ambulance"
          />
        </RadioGroup>

        <Divider sx={{ my: 3 }} />

        {/* ================= ADDRESS ================= */}
        <Typography variant="subtitle1" mb={1}>
          Address
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              value={address}
              onChange={(e) =>
                setAddress(e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="City"
              value={city}
              onChange={(e) =>
                setCity(e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="State"
              value={state}
              onChange={(e) =>
                setState(e.target.value)
              }
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ================= CONTACT ================= */}
        <Typography variant="subtitle1" mb={1}>
          Contact Person
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Contact Name"
              value={contactName}
              onChange={(e) =>
                setContactName(e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Phone"
              value={phone}
              onChange={(e) =>
                setPhone(e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) =>
                setEmail(e.target.value)
              }
            />
          </Grid>
        </Grid>

        {/* ================= ACTIONS ================= */}
        <Box
          mt={4}
          display="flex"
          gap={2}
          justifyContent="flex-end"
        >
          <Button
            variant="outlined"
            onClick={() => navigate("/partners")}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Partner"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddPartner;
